import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  IconButton,
  TablePagination,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Grid2";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import { PartialBlob } from "../types";
import { isBlobArchived, isBlobDeleted } from "../utils/blob-status";
import { formatDate, formatFileSize } from "../utils/formatters";
import { getFileIconComponent } from "../utils/file-icon";

const StyledCard = styled(Card)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  height: "100%",
  padding: theme.spacing(2),
  position: "relative",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: theme.shadows[6],
  },
}));

interface FileIconGridProps {
  blobs: PartialBlob[];
  selectedBlobs: PartialBlob[];
  onSelectionChange: (blobs: PartialBlob[]) => void;
  onDownload: (blob: PartialBlob) => void;
  onDelete: (blob: PartialBlob) => void;
  onRehydrate: (blob: PartialBlob) => void;
}

const FileIconGrid = ({
  blobs,
  selectedBlobs,
  onSelectionChange,
  onDownload,
  onDelete,
  onRehydrate,
}: FileIconGridProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setPage(0);
  }, [blobs]);

  const isSelected = (blob: PartialBlob) =>
    selectedBlobs.some((b) => b.name === blob.name);

  const toggleSelection = (blob: PartialBlob, checked: boolean) => {
    if (checked) {
      onSelectionChange([...selectedBlobs, blob]);
    } else {
      onSelectionChange(selectedBlobs.filter((b) => b.name !== blob.name));
    }
  };

  if (!blobs || blobs.length === 0) {
    return <Alert severity="info">No files found in this folder.</Alert>;
  }

  const pagedBlobs = blobs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box>
      <Grid container spacing={2}>
        {pagedBlobs.map((blob) => {
          const deleted = isBlobDeleted(blob);
          const archived = isBlobArchived(blob);
          const Icon = getFileIconComponent(
            blob.blobModifiedInformation?.contentType,
          );

          let downloadTooltip = "Download";
          if (deleted) downloadTooltip = "This file has been deleted";
          else if (archived)
            downloadTooltip = "Rehydrate this file before downloading";

          return (
            <Grid key={blob.name} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <StyledCard variant="outlined">
                <Checkbox
                  size="small"
                  checked={isSelected(blob)}
                  disabled={deleted}
                  onChange={(e) => toggleSelection(blob, e.target.checked)}
                  inputProps={{ "aria-label": `Select ${blob.name}` }}
                  sx={{ position: "absolute", top: 4, left: 4 }}
                />
                <Icon
                  sx={{ fontSize: 56, color: "primary.main", mb: 1, mt: 2 }}
                />
                <CardContent sx={{ padding: "0 !important", width: "100%" }}>
                  <Typography variant="subtitle2" noWrap title={blob.name}>
                    {blob.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {formatFileSize(blob.blobModifiedInformation?.size)}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    {formatDate(blob.blobModifiedInformation?.createdOn)}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {deleted ? (
                      <Chip label="Deleted" size="small" />
                    ) : (
                      <Chip
                        label={
                          blob.blobModifiedInformation?.status?.statusName ??
                          "N/A"
                        }
                        color={archived ? "default" : "success"}
                        size="small"
                      />
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 0.5,
                      mt: 1,
                    }}
                  >
                    <Tooltip title={downloadTooltip}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Download ${blob.name}`}
                          disabled={deleted || archived}
                          onClick={() => onDownload(blob)}
                          sx={{ color: "primary.main" }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {archived && !deleted && (
                      <Tooltip title="Rehydrate from archive">
                        <IconButton
                          size="small"
                          aria-label={`Rehydrate ${blob.name}`}
                          onClick={() => onRehydrate(blob)}
                          sx={{ color: "info.main" }}
                        >
                          <CloudSyncIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip
                      title={deleted ? "This file has been deleted" : "Delete"}
                    >
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Delete ${blob.name}`}
                          disabled={deleted}
                          onClick={() => onDelete(blob)}
                          sx={{ color: "error.main" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </CardContent>
              </StyledCard>
            </Grid>
          );
        })}
      </Grid>
      <TablePagination
        component="div"
        count={blobs.length}
        page={page}
        onPageChange={(_event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number.parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 15, 25, 50, 500]}
      />
    </Box>
  );
};

export default FileIconGrid;
